import { Injectable } from "@angular/core";
import { PostEventModel } from "../../admin/events-screen/event-page-models/post-event-model";
import { InternalEventFormModel } from "../../admin/events-screen/event-page-models/internal-event-form-model";
import {
  EvenFormDynamicFieldAction,
  EventFormDynamicField,
} from "../../admin/events-screen/event-page-models/event-form-dynamic-field";
import { HebrewNumbering } from "../../admin/events-screen/utils/hebrewNumbering";
import { EventFormValidationSchema } from "../../admin/events-screen/event-page-models/event-form-validation-schema";
import { EventFormEventType } from "../../admin/events-screen/event-page-models/event-form-event-type";
import { prettyPrintJsonAsYamlList } from "../../admin/events-screen/utils/prettyPrintJsonAsYamlList";
import { EventFormStatusEnum } from "../../admin/events-screen/event-page-models/event-form-status.enum";
import {
  ParentQuestion,
  ParentQuestionType,
} from "../../admin/events-screen/event-page-models/parent-question";

@Injectable()
export class EventValidationService {
  public static getComplexValidationRule(
    ruleName: keyof EventFormValidationSchema,
    postEventFormData: PostEventModel
  ): boolean {
    switch (ruleName) {
      case "mustHaveRegistration_period":
        const childEventsHaveBrotherDiscount =
          postEventFormData.child_events.some((c) => c.has_brother_discount);
        return (
          childEventsHaveBrotherDiscount ||
          postEventFormData.has_brother_discount
        );
    }
    return true;
  }
  public static getEventType(
    postEventFormData: PostEventModel
  ): EventFormEventType {
    if (
      postEventFormData.event_type &&
      Object.values(EventFormEventType).includes(
        +postEventFormData.event_type as EventFormEventType
      )
    ) {
      return +postEventFormData.event_type;
    }

    return EventFormEventType.General;
  }
  public static buildValidationRulesSchema(
    postEventFormData: PostEventModel,
    formStatus: EventFormStatusEnum
  ): EventFormValidationSchema {
    const eventType = EventValidationService.getEventType(postEventFormData);
    const basicRules: EventFormValidationSchema = {
      mustHaveBudgetSection: eventType !== EventFormEventType.Michlol,
      childEventStartDateSmallerThanEndDate: true,
      childRegistrationStartDateSmallerThanEndDate: true,
      eventStartDateSmallerThanEndDate: true,
      mustHaveRegistration_start_date: true,
      mustHaveRegistration_end_date: true,
      mustHAveEventEndDate: true,
      mustHAveEventStartDate: true,
      mustHaveChildEventGrades: true,
      mustHaveChildEventKenim: true,
      mustHaveChildEventPrice: eventType !== EventFormEventType.Michlol,
      mustHaveDescription: true,
      mustHaveEventType: true,
      mustHaveName: true,
      eventIndexCantBeEmpty: formStatus === EventFormStatusEnum.Update,
      gainCenter_1_cantBeEmpty: formStatus === EventFormStatusEnum.Update,
      mustHaveRegistration_period:
        EventValidationService.getComplexValidationRule(
          "mustHaveRegistration_period",
          postEventFormData
        ),
      registrationStartDateSmallerThanEndDate: true,
      questionOfTypeApprovalMustHaveOneAnswer: true,
    };

    switch (eventType) {
      case EventFormEventType.Michlol:
        break;
    }

    return basicRules;
  }
  public static validateMegaForm(
    postData: PostEventModel,
    formStatus: EventFormStatusEnum,
    allParentQuestions: ParentQuestion[],
    specialEventName: string
  ): string[] {
    const getHebrewFromIndex = (i: number) => {
      return postData.child_events.length > 1 ? HebrewNumbering[i] + "'" : "";
    };

    const rules = EventValidationService.buildValidationRulesSchema(
      postData,
      formStatus
    );
    const validationErrors = [];
    if (rules.mustHaveBudgetSection && !postData.budget_item_line) {
      validationErrors.push(`יש למלא סעיף תקציבי  `);
    }
    if (rules.mustHaveEventType && !postData.event_type) {
      validationErrors.push(`יש למלא סוג ${specialEventName}`);
    }
    if (rules.mustHAveEventEndDate && !postData.event_end_date) {
      validationErrors.push(`יש למלא תאריך סיום ${specialEventName}`);
    }
    if (rules.mustHaveDescription && !postData.description) {
      validationErrors.push(`יש למלא  תאור ${specialEventName}`);
    }
    if (rules.mustHaveName && !postData.name) {
      validationErrors.push(`יש למלא שם ${specialEventName}`);
    }

    if (
      rules.mustHaveRegistration_start_date &&
      !postData.registration_start_date
    ) {
      validationErrors.push(`יש למלא תאריך תחילת הרשמה`);
    }
    if (
      rules.mustHaveRegistration_end_date &&
      !postData.registration_end_date
    ) {
      validationErrors.push(`יש למלא תאריך סיום הרשמה`);
    }
    if (
      rules.eventStartDateSmallerThanEndDate &&
      +postData.event_start_date &&
      +postData.event_end_date &&
      +postData.event_start_date > +postData.event_end_date
    ) {
      validationErrors.push(
        `תאריך תחילת ה${specialEventName} צריך להיות לפני תאריך סיום ה${specialEventName}   `
      );
    }
    if (
      rules.registrationStartDateSmallerThanEndDate &&
      +postData.registration_start_date &&
      +postData.registration_end_date &&
      +postData.registration_start_date > +postData.registration_end_date
    ) {
      validationErrors.push(
        `תאריך תחילת ההרשמה צריך להיות לפני תאריך סיום ההרשמה   `
      );
    }

    if (rules.mustHaveRegistration_period && !postData.registration_period) {
      validationErrors.push(`יש למלא תקופת הרשמה `);
    }
    if (rules.gainCenter_1_cantBeEmpty && !postData.merkaz_revach_1) {
      validationErrors.push(`יש למלא מרכז רווח 1 `);
    }
    if (rules.eventIndexCantBeEmpty && !postData.priority_index) {
      validationErrors.push(`יש למלא אינדקס `);
    }

    postData.child_events.forEach((childEvent, i) => {
      const pricingDescription = ` בתמחור ` + getHebrewFromIndex(i);

      if (rules.mustHaveChildEventPrice && childEvent.price === null) {
        validationErrors.push(
          `יש למלא מחיר ${specialEventName}` + pricingDescription
        );
      }
      if (rules.mustHaveChildEventGrades && !childEvent.grades.length) {
        validationErrors.push(`יש למלא שכבות` + pricingDescription);
      }
      if (rules.mustHaveChildEventKenim && !childEvent.kenim.length) {
        validationErrors.push(`יש לבחור קינים` + pricingDescription);
      }
      if (
        rules.childRegistrationStartDateSmallerThanEndDate &&
        +childEvent.registration_start_date &&
        +childEvent.registration_end_date &&
        +childEvent.registration_start_date > +childEvent.registration_end_date
      ) {
        validationErrors.push(
          `תאריך תחילת ההרשמה צריך להיות לפני תאריך סיום ההרשמה   ` +
            pricingDescription
        );
      }
      if (
        rules.childEventStartDateSmallerThanEndDate &&
        +childEvent.event_start_date &&
        +childEvent.event_end_date &&
        +childEvent.event_start_date > +childEvent.event_end_date
      ) {
        validationErrors.push(
          `תאריך תחילת ה${specialEventName} צריך להיות לפני תאריך סיום ה${specialEventName}   `,
          pricingDescription
        );
      }
      if (rules.questionOfTypeApprovalMustHaveOneAnswer) {
        const aprrovalQuestionMissingOption = allParentQuestions.some((q) => {
          if (q.questionsType === ParentQuestionType.CheckBox) {
            const firstOptionLabel =
              (q.options && q.options[0] && q.options[0].label) || ``;
            return !firstOptionLabel.length;
          }
          return false;
        });
        if (aprrovalQuestionMissingOption) {
          validationErrors.push(
            "בשאלות מסוג אישור, יש לכתוב תשובה אחת שאותה מסמן ההורה "
          );
        }
      }
    });
    return validationErrors.map((valError) => valError.trim());
  }
  public static buildEventDynamicFieldsRules(formData: InternalEventFormModel) {
    const rules: EventFormDynamicField[] = [];
    if (formData.parentEventMain && formData.AllChildEvents) {
    } else {
      return;
    }

    if (!formData.parentEventMain.parentEventDetails.brothersDiscount) {
      rules.push({
        fieldName: "brothersDiscountPeriod",
        rules: [{ statusChange: EvenFormDynamicFieldAction.hide }],
      });
    }
    return rules;
  }
  public static async convertServer400ErrorsToReadableText(
    errorResponse: Response
  ): Promise<string> {
    if (!errorResponse) {
      return "Unknown error";
    }
    if (errorResponse.status === 400) {
      const body = await errorResponse.json();
      return prettyPrintJsonAsYamlList(body);
    }
    return errorResponse.toString();
  }
}



// WEBPACK FOOTER //
// ./src/app/_services/eventsServices/eventValidation.service.ts